/* global Word */

import type { LayoutConfig, TemplateProfile } from "../../../templates/types";
import type { ExhibitInsertionStrategy } from "../types";
import { TableBuilder } from "../../builders/TableBuilder";
import { setupHeaderCell, setupTablePasteCell, setupSourceCell } from "../cellSetup";

/**
 * Inserts a double table exhibit (3 rows × 3 columns: table | sep | table).
 * Columns: 0 = left table, 1 = separator, 2 = right table.
 */
export class DoubleTableStrategy implements ExhibitInsertionStrategy {
  readonly layoutType = "double-table" as const;

  async execute(
    context: Word.RequestContext,
    anchorRange: Word.Range,
    config: LayoutConfig,
    profile: TemplateProfile,
    startingExhibitNumber: number
  ): Promise<Word.Table> {
    const sepWidth = config.sepWidth ?? 11; // fallback 11pt if profile omits it
    const totalWidth = config.colWidth * 2 + sepWidth;

    // ── 1. Insert 3×3 table shell ────────────────────────────────────────────
    const table = anchorRange.insertTable(3, 3, Word.InsertLocation.after);
    TableBuilder.clearAllBorders(table);
    TableBuilder.lockWidth(table, totalWidth);

    // ── 2. FIRST row load+sync — for height setting ──────────────────────────
    table.rows.load("items");
    await context.sync();

    const rowsForHeight = table.rows.items;
    rowsForHeight[0].setHeight(config.row1Height, Word.RowHeightRule.atLeast);
    rowsForHeight[1].setHeight(config.row2Height, Word.RowHeightRule.atLeast);
    rowsForHeight[2].setHeight(config.row3Height, Word.RowHeightRule.atLeast);

    // ── 4. SECOND row load+sync — mandatory after height mutation ────────────
    table.rows.load("items");
    await context.sync();

    const rows = table.rows.items;

    // ── 5. Load ALL cells across all three rows in one batch ─────────────────
    rows[0].cells.load("items");
    rows[1].cells.load("items");
    rows[2].cells.load("items");
    await context.sync();

    // ── 6. Zero-pad separator cells (column 1) ───────────────────────────────
    TableBuilder.zeroCellPadding(rows[0].cells.items[1]);
    TableBuilder.zeroCellPadding(rows[1].cells.items[1]);
    TableBuilder.zeroCellPadding(rows[2].cells.items[1]);

    // ── 7. Setup left table column (col 0) ───────────────────────────────────
    await setupHeaderCell(context, rows[0].cells.items[0], startingExhibitNumber, profile, { leftIndent: 2 });
    await setupTablePasteCell(context, rows[1].cells.items[0], { height: config.row2Height, width: config.colWidth });
    await setupSourceCell(context, rows[2].cells.items[0], profile, { leftIndent: 2 });

    // ── 8. Setup right table column (col 2) ──────────────────────────────────
    await setupHeaderCell(context, rows[0].cells.items[2], startingExhibitNumber + 1, profile, { leftIndent: 2 });
    await setupTablePasteCell(context, rows[1].cells.items[2], { height: config.row2Height, width: config.colWidth });
    await setupSourceCell(context, rows[2].cells.items[2], profile, { leftIndent: 2 });

    // ── 9. Lock cell widths after all content is populated to prevent Word reflow ──
    for (let rowIndex = 0; rowIndex < 3; rowIndex++) {
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[0], config.colWidth);
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[1], sepWidth);
      TableBuilder.lockColumnWidth(rows[rowIndex].cells.items[2], config.colWidth);
    }

    return table;
  }
}
